import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, EditOutlined } from "@ant-design/icons"
import { Skeleton } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { Loadable } from "../../hooks/use-tracking-overview.ts"
import type { TileId, TrackingTile } from "../../types/tracking.types.ts"
import { TILE_IDS } from "../../types/tracking.types.ts"

const PRESENTATION: Record<TileId, { icon: React.ReactNode; tint: string; higherIsBetter: boolean | null }> = {
  out_for_signature: { icon: <ClockCircleOutlined />, tint: "#2A7D8C", higherIsBetter: null },
  awaiting_countersignature: { icon: <EditOutlined />, tint: "#B45309", higherIsBetter: false },
  fully_signed_month: { icon: <CheckCircleOutlined />, tint: "#16A34A", higherIsBetter: true },
  declined_or_expired_month: { icon: <CloseCircleOutlined />, tint: "#B91C1C", higherIsBetter: false }
}
const TONE = { good: "#16A34A", bad: "#EF4444", flat: "#94A3B8" } as const

function Delta({ tile }: { tile: TrackingTile }) {
  const { t } = useTranslation()
  if (!tile.compare) return null
  const { delta } = tile.compare
  const better = PRESENTATION[tile.id].higherIsBetter
  const tone = delta === 0 || better === null ? "flat" : (delta > 0) === better ? "good" : "bad"
  const signed = delta > 0 ? `+${delta}` : String(delta)
  return (
    <span style={{ color: TONE[tone] }} className={"text-[11px] font-semibold"}>
      {t("esign.tracking.tiles.compare", { delta: signed })}
    </span>
  )
}

interface Props {
  stats: Loadable<TrackingTile[]>
  selected: TileId | null
  /** The tile to show, or null to let the pressed one go. */
  onSelect: (tile: TileId | null) => void
}

export function TrackingKpiRow({ stats, selected, onSelect }: Props) {
  const { t } = useTranslation()
  const byId = new Map((stats.data ?? []).map((tile) => [tile.id, tile]))

  return (
    <div className={"grid gap-3"} style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
      {TILE_IDS.map((id) => {
        const tile = byId.get(id)
        const { icon, tint } = PRESENTATION[id]
        // The drill does not need the count, so a tile stays usable when the figures failed.
        const drill = () => onSelect(selected === id ? null : id)
        return (
          <div
            key={id}
            data-testid={"tracking-kpi-tile"}
            role={"button"}
            tabIndex={0}
            aria-pressed={selected === id}
            onClick={drill}
            onKeyDown={(event) => {
              if ((event.key !== "Enter" && event.key !== " ")) return
              event.preventDefault()
              drill()
            }}
            className={[
              "rounded-xl border bg-white px-4 py-3",
              selected === id ? "border-blue-500 ring-1 ring-blue-500" : "border-slate-200",
              "cursor-pointer hover:border-slate-300"
            ].join(" ")}
          >
            <div className={"flex items-center gap-2"}>
              <span style={{ color: tint, lineHeight: 0 }}>{icon}</span>
              <span className={"truncate text-[13px] font-medium text-slate-600"}>{t(`esign.tracking.tiles.${id}`)}</span>
            </div>
            {stats.status === "loading" && !tile ? (
              <Skeleton.Node active style={{ width: 72, height: 34, marginTop: 6 }} />
            ) : (
              <div data-testid={"tracking-kpi-value"} className={"mt-1 text-[30px] font-bold leading-tight text-slate-900"}>
                {stats.status === "error" ? "—" : (tile?.value ?? 0)}
              </div>
            )}
            <div className={"mt-0.5 min-h-[16px]"}>{tile && stats.status !== "error" && <Delta tile={tile} />}</div>
          </div>
        )
      })}
    </div>
  )
}
